import { useEffect } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { InAppTemplate } from "../../../shared/templates/inapp";
import PostEditor from "./components/PostEditor";
import {
  SuccessModal,
  ConfirmationModal,
  ErrorModal,
} from "../../../shared/components/modals";
import { BlogService } from "../../../services/blogService";
import {
  adaptBlogPostToCreateCommand,
  UIBlogPost,
} from "../../../services/blogAdapters";
import "./blog.scss";
import "./blog-editor.scss";

type BlogPost = UIBlogPost;

const AddNewBlogPost = () => {
  const navigate = useNavigate();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [preSelectedCategory, setPreSelectedCategory] = useState<string>("");

  const queryParams = new URLSearchParams(window.location.search);
  const categoryIdParam = queryParams.get("category");

  useEffect(() => {
    const fetchCategory = async () => {
      if (categoryIdParam) {
        try {
          const category = await BlogService.getBlogCategoryById(
            categoryIdParam
          );
          if (category) {
            setPreSelectedCategory(category.id);
          }
        } catch (error) {
            }
      }
    };

    fetchCategory();
  }, [categoryIdParam]);

  const initialPost: Partial<BlogPost> = {
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    featuredImage: "",
    categoryIds: preSelectedCategory ? [preSelectedCategory] : [],
    tags: [],
    metaTitle: "",
    metaDescription: "",
    status: "draft",
    author: {
      name: "Admin",
      avatar: "",
    },
    publishDate: new Date().toISOString(),
    readTime: "5 min read",
    views: 0,
    featured: false,
  };

  const handleSave = async (post: Partial<BlogPost>, isDraft: boolean) => {
    try {
      setSaving(true);

      const postToSave: UIBlogPost = {
        ...initialPost,
        ...post,
        status: isDraft ? "draft" : "published",
      } as UIBlogPost;

      const createCommand = adaptBlogPostToCreateCommand(postToSave);
      await BlogService.createBlogPost(createCommand);

      setSuccessMessage(
        `Post ${isDraft ? "saved as draft" : "published"} successfully!`
      );
      setShowSuccessModal(true);
    } catch (error) {
      setErrorMessage("Failed to save post. Please try again.");
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmDiscard = () => {
    navigate("/app/blog");
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate("/app/blog");
  };

  return (
    <>
      <InAppTemplate
        pageHeadingTitle="Add New Post"
        childComponent={
          <div className="blog-editor-page">
            <PostEditor
              post={initialPost}
              onSave={handleSave}
              onCancel={handleCancel}
              isNew={true}
            />
          </div>
        }
      />

      <SuccessModal
        show={showSuccessModal}
        onHide={handleSuccessModalClose}
        title="Success!"
        message={successMessage}
        actionText="Continue"
        autoClose={false}
      />

      <ConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmDiscard}
        title="Discard Post"
        message="Are you sure you want to discard this post? All changes will be lost."
        confirmText="Discard"
        cancelText="Keep Editing"
        variant="warning"
      />

      <ErrorModal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        title="Error"
        message={errorMessage}
        actionText="OK"
      />
    </>
  );
};

export default AddNewBlogPost;
